/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { Animation, Img, Link, Typography } from '@zapier/design-system';

import { useUniversalNavContext } from '../../../../hooks/useUniversalNavContext';
import type { ArticleStubType } from '../../../../constants';
import { emitUserInteractionEvent } from '../../../../utils/avroEvents';
import { ArrowLinkIcon } from '../..';

type Props = ArticleStubType & {
  /**
   * Function called when closing menu on a link from within the MegaMenu
   */
  onCloseMenu?: () => void;
};

const Styles = {
  root: css({
    color: 'var(--zds-gray-warm-10, #201515)',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    maxWidth: 400,
    font: `var(--zds-small-print-4, 700 13px/20px ${Typography.base.fontFamily})`,
    '@media (hover: hover)': {
      '&:hover p': {
        textDecoration: 'underline',
      },
      '&:hover a': {
        textDecoration: 'none',
      },
      '&:hover p > span': {
        marginLeft: 4,
        opacity: 1,
      },
      '&:focus-within': {
        outline: `1px solid var(--zds-ui-primary-strongest, #503EBD)`,
      },
      '&:focus-within > a': {
        textDecoration: 'none',
      },
    },
  }),
  title: css({
    display: 'block',
    marginBottom: 20,
    textDecoration: 'none',
    color: 'var(--zds-gray-warm-10, #201515)',
    outline: 'none',
    // This overrides an scss style that exists in `z/z`
    borderBottom: 'none',
    '&:hover': {
      textDecoration: 'none',
      color: 'var(--zds-gray-warm-10, #201515)',
    },
    '&:focus': {
      textDecoration: 'none',
      color: 'var(--zds-gray-warm-10, #201515)',
    },
    // This pseudo element stretches over the entire PromoArticle
    // to make the entire PromoArticle clickable
    '&::before': {
      content: '""',
      position: 'absolute',
      zIndex: 1,
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  }),
  imageContainer: css({
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  }),
  image: css({
    minWidth: '88px',
  }),
  description: css({
    transition: `text-decoration 100ms ${Animation.transitionTimingFunction}`,
    // All of the properties below this are necessary
    // for line clamping to work. Even though the properties
    // have 'webkit' in them, they work cross-browser.
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 4,
    overflow: 'hidden',
    font: `var(--zds-small-print-1, 400 13px/20px ${Typography.base.fontFamily})`,
  }),
};

/**
 * Article with an Image and description
 */
export const ArticlePromo = (props: Props) => {
  const { getFullyQualifiedURL } = useUniversalNavContext();

  const handleEvent = (text: string, eventType: string) => {
    emitUserInteractionEvent({
      event_action: eventType,
      option_selected: `ArticlePromo: ${text}`,
    });
    if (eventType === 'click' && props.onCloseMenu) props.onCloseMenu();
  };

  return (
    <article css={Styles.root} data-zds>
      <Link
        color={null}
        css={Styles.title}
        href={getFullyQualifiedURL(props.href) || undefined}
        onClick={() => handleEvent(props.text, 'click')}
        onMouseEnter={() => handleEvent(props.text, 'hover')}
        target={props.target}
      >
        {props.text}
      </Link>
      <div css={Styles.imageContainer} data-zds>
        <Img
          css={Styles.image}
          alt={props.altText}
          borderRadius={0}
          src={props.imageUrl}
          width={100}
        />
        <p css={Styles.description} data-zds>
          {props.description}
          <ArrowLinkIcon />
        </p>
      </div>
    </article>
  );
};
